'use strict';
import get from 'lodash/get';
import noop from 'lodash/noop';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import {getDomainLink, getLink} from '../../core-utilities/domain/ReduxUtils';
import {eventToObservable} from '../../core-utilities/rx-utils';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import * as TASK_TYPE from '../../tasks/enums/TASK_TYPE';
import {RoleBinding} from './RoleBinding';
import {Role} from './Role';
import {assetTaskRoleData, orgRoleData, projectRoleData, taskRoleData} from './RoleData';


PermissionsService.$inject = [
  'AuthService',
  'OrganizationDomain',
  'ProjectDomain',
  'TaskDomain',
  'AssetTaskDomain',
  'rxPermissionsService',
];

/*
 primary functions:
 - check role async
 - check role sync
 - watch role
 */

export function PermissionsService(
  AuthService,
  OrganizationDomain,
  ProjectDomain,
  TaskDomain,
  AssetTaskDomain,
  rxPermissionsService,
) {
  this.getTargetForProjectId = getTargetForProjectId;
  this.getTargetForProject = getTargetForProject;
  this.getTargetForTaskId = getTargetForTaskId;
  this.getTargetForTask = getTargetForTask;
  this.getTargetForAssetTaskId = getTargetForAssetTaskId;
  this.getTargetForAssetTask = getTargetForAssetTask;

  this.checkOrganizationPermission = checkOrganizationPermission;
  this.checkProjectPermission = checkProjectPermission;
  this.checkTaskPermission = checkTaskPermission;
  this.checkAssetTaskPermission = checkAssetTaskPermission;

  this.retrieveRole = rxPermissionsService.retrieveRole;
  this.retrieveProjectRole = rxPermissionsService.retrieveProjectRole;
  this.retrieveTaskRole = rxPermissionsService.retrieveTaskRole;
  this.retrieveAssetTaskRole = rxPermissionsService.retrieveAssetTaskRole;

  this.permissionPrefix = permissionPrefix;
  this.getTasksPermission = getTasksPermission;

  this.getForOrg = getForOrg;
  this.getForProject = getForProject;
  this.getForTask = getForTask;
  this.getForAssetTask = getForAssetTask;

  /** @deprecated */
  this.bindOrg = bindOrg;
  /** @deprecated */
  this.bindProject = bindProject;
  /** @deprecated */
  this.bindTask = bindTask;
  /** @deprecated */
  this.bindAssetTask = bindAssetTask;

  this.orgBinding = orgBinding;
  this.projectBinding = projectBinding;
  this.taskBinding = taskBinding;
  this.assetTaskBinding = assetTaskBinding;

  // get target

  function getTargetForProjectId(id) {
    return ProjectDomain.$get(id).then(getTargetForProject);
  }

  function getTargetForProject(project) {
    if (project.isPrivate) {
      return Promise.resolve(project);
    } else {
      return getDomainLink(project, 'organization');
    }
  }

  function getTargetForTaskId(id) {
    return TaskDomain.$get(id).then(getTargetForTask);
  }

  function getTargetForTask(task) {
    if (task.isPrivate) {
      return Promise.resolve(task);
    } else {
      return getDomainLink(task, 'project').then(getTargetForProject);
    }
  }

  function getTargetForAssetTaskId(id) {
    return AssetTaskDomain.$get(id).then(getTargetForAssetTask);
  }

  function getTargetForAssetTask(task) {
    const parentTaskId = getLink(task, 'task').id;
    if (!parentTaskId && task.isPrivate) {
      return Promise.resolve(task);
    } else if (parentTaskId) {
      return getDomainLink(task, 'task').then(getTargetForTask);
    } else {
      return getDomainLink(task, 'project').then(getTargetForProject);
    }
  }

  // Get RoleData for target once, as a promise.

  // Organization

  // RoleData only
  function getOrgRoleData(orgId) {
    return OrganizationDomain.$getMembersRelation(orgId, AuthService.getUserId())
      .then(member => get(member, 'role.id') ? rxPermissionsService.retrieveRole(member.role.id) : null)
      .then(role => orgRoleData(role))
      .catch(err => orgRoleData(null));
  }

  // RoleData + permissions
  function getForOrg(VIEW_ACTIONS, orgId) {
    return getOrgRoleData(orgId)
      .then(roleData => ({
        roleData,
        permissions: rxPermissionsService.calculatePermissions(
          VIEW_ACTIONS,
          roleData,
          rxPermissionsService.hasOrgPermission,
        ),
      }));
  }

  // Check a single permission
  function checkOrganizationPermission(orgId, permission) {
    return getOrgRoleData(orgId)
      .then(roleData => rxPermissionsService.hasOrgPermission(roleData, permission))
      .catch(__onError);
  }

  // Project

  // RoleData only
  function getProjectRoleData(projectId) {
    return ProjectDomain.$get(projectId)
      .then(project =>
        Promise.all([
          getOrgRoleData(getLink(project, 'organization').id),
          !project.isPrivate
            ? Promise.resolve(projectRoleData(null, false))
            : ProjectDomain.$getMembersRelation(projectId, AuthService.getUserId())
              .then(member => get(member, 'role.id') ? rxPermissionsService.retrieveProjectRole(member.role.id) : null)
              .then(role => projectRoleData(role, true))
              .catch(err => projectRoleData(null, true)),
        ])
        /* Combine the RoleData from each source into a single object */
      )
      .then(data => Object.assign({}, ...data));
  }

  // RoleData + permissions
  function getForProject(VIEW_ACTIONS, projectId) {
    return getProjectRoleData(projectId)
      .then(roleData => ({
        roleData,
        permissions: rxPermissionsService.calculatePermissions(
          VIEW_ACTIONS,
          roleData,
          rxPermissionsService.hasProjectPermission,
        ),
      }));
  }

  // Check a single permission
  function checkProjectPermission(projectId, permission) {
    return getProjectRoleData(projectId)
      .then(roleData => rxPermissionsService.hasProjectPermission(roleData, permission))
      .catch(__onError);
  }

  // Task

  // RoleData only
  function getTaskRoleData(taskId) {
    return TaskDomain.$get(taskId)
      .then(task =>
        Promise.all([
          getProjectRoleData(getLink(task, 'project').id),
          !task.isPrivate
            ? Promise.resolve(taskRoleData(null, false))
            : TaskDomain.$getMembersRelation(taskId, AuthService.getUserId())
              .then(member => get(member, 'role.id') ? rxPermissionsService.retrieveTaskRole(member.role.id) : null)
              .then(role => taskRoleData(role, true))
              .catch(err => taskRoleData(null, true)),
        ])
        /* Combine the RoleData from each source into a single object */
      )
      .then(data => Object.assign({}, ...data));
  }

  // RoleData + permissions
  function getForTask(VIEW_ACTIONS, task, taskId) {
    return getTaskRoleData(taskId)
      .then(roleData => ({
        roleData,
        permissions: rxPermissionsService.calculatePermissions(
          VIEW_ACTIONS,
          roleData,
          rxPermissionsService.hasTaskPermission,
        ),
      }));
  }

  // Check a single permission
  function checkTaskPermission(taskId, permission) {
    return getTaskRoleData(taskId)
      .then(roleData => rxPermissionsService.hasTaskPermission(roleData, permission))
      .catch(__onError);
  }

  // Asset Task

  // RoleData only
  function getAssetTaskRoleData(assetTaskId) {
    return AssetTaskDomain.$get(assetTaskId)
      .then(assetTask => {
        const projectId = getLink(assetTask, 'project').id;
        const taskId = getLink(assetTask, 'task').id;

        return Promise.all([
          getProjectRoleData(projectId),
          taskId ? getTaskRoleData(taskId) : Promise.resolve(null),
          !assetTask.isPrivate
            ? Promise.resolve(assetTaskRoleData(null, false))
            : AssetTaskDomain.$getMembersRelation(assetTaskId, AuthService.getUserId())
            /* eslint-disable-next-line max-len */
              .then(member => get(member, 'role.id') ? rxPermissionsService.retrieveAssetTaskRole(member.role.id) : null)
              .then(role => assetTaskRoleData(role, true))
              .catch(err => assetTaskRoleData(null, true)),
        ]);
      })
      .then(data => Object.assign({}, ...data));
  }

  // RoleData + permissions
  function getForAssetTask(VIEW_ACTIONS, task, taskId) {
    return getAssetTaskRoleData(taskId)
      .then(roleData => ({
        roleData,
        permissions: rxPermissionsService.calculatePermissions(
          VIEW_ACTIONS,
          roleData,
          rxPermissionsService.hasAssetTaskPermission,
        ),
      }));
  }

  // Check a single permission
  function checkAssetTaskPermission(taskId, permission) {
    return getAssetTaskRoleData(taskId)
      .then(roleData => rxPermissionsService.hasTaskPermission(roleData, permission))
      .catch(__onError);
  }


  // Helper Methods

  function permissionPrefix(taskType) {
    switch (taskType) {
      case TASK_TYPE.SITE:
        return 'asset_';
      case TASK_TYPE.PROJECT:
        return '';
      case TASK_TYPE.PROJECT_SITE:
        return 'template_';
    }
  }

  function getTasksPermission(taskType, permission) {
    switch (taskType) {
      case TASK_TYPE.SITE:
        return 'asset_project_' + permission;
      case TASK_TYPE.PROJECT:
        return 'project_' + permission;
      case TASK_TYPE.PROJECT_SITE:
        return 'project_template_' + permission;
    }
  }


  // Legacy Angular bind-to-scope Helper Methods

  /**
   * Adds Object.<string, boolean> vm.permissions which maps actions to whether the user
   * is currently permitted to perform them
   * Adds function vm.hasPermission
   * Adds {@link Role} object vm.orgRole.
   * @param {$rootScope.Scope} $scope
   * @param {Object} vm
   * @param {Object} VIEW_ACTIONS Map of action name to required permission
   * @return {Subscription}
   * @deprecated
   */
  function bindOrg($scope, vm, VIEW_ACTIONS) {
    vm.permissions = {};

    vm.hasPermission = noop;

    return rxPermissionsService.userOrgRoleData$
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .subscribe(orgBinding($scope, vm, VIEW_ACTIONS));
  }

  function orgBinding($scope, vm, VIEW_ACTIONS) {
    const stateFn = rxPermissionsService.orgState(VIEW_ACTIONS);
    return roleData => $scope.$evalAsync(() => Object.assign(vm, stateFn(roleData)));
  }

  /**
   * Adds Object.<string, boolean> vm.permissions which maps actions to whether the user
   * is currently permitted to perform them
   * Adds function vm.hasPermission
   * Adds {@link Role} objects vm.orgRole and vm.projectRole.
   * @param {$rootScope.Scope} $scope
   * @param {Object} vm
   * @param {Object} VIEW_ACTIONS Map of action name to required permission
   * @return {Subscription}
   * @deprecated
   */
  function bindProject($scope, vm, VIEW_ACTIONS) {
    vm.permissions = {};

    vm.hasPermission = noop;

    return rxPermissionsService.userProjectRoleData$
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .subscribe(projectBinding($scope, vm, VIEW_ACTIONS));
  }

  function projectBinding($scope, vm, VIEW_ACTIONS) {
    const stateFn = rxPermissionsService.projectState(VIEW_ACTIONS);
    return roleData => $scope.$evalAsync(() => Object.assign(vm, stateFn(roleData)));
  }

  /**
   * Adds Object.<string, boolean> vm.permissions which maps actions to whether the user
   * is currently permitted to perform them
   * Adds function vm.hasPermission
   * Adds {@link Role} objects vm.orgRole, vm.projectRole, and vm.taskRole.
   * @param {$rootScope.Scope} $scope
   * @param {Object} vm
   * @param {Object} VIEW_ACTIONS Map of action name to required permission
   * @param {modelGetter|Object} taskGetter
   * @return {Subscription}
   * @deprecated
   */
  function bindTask($scope, vm, VIEW_ACTIONS, taskGetter) {
    vm.permissions = {};

    vm.hasPermission = noop;

    const task$ = RoleBinding.legacyModelToObservable($scope, taskGetter);

    return rxPermissionsService.buildUserTaskRoleData$(task$)
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .subscribe(taskBinding($scope, vm, VIEW_ACTIONS));
  }

  function taskBinding($scope, vm, VIEW_ACTIONS) {
    const stateFn = rxPermissionsService.taskState(VIEW_ACTIONS);
    return roleData => $scope.$evalAsync(() => Object.assign(vm, stateFn(roleData)));
  }

  /**
   * Adds Object.<string, boolean> vm.permissions which maps actions to whether the user
   * is currently permitted to perform them
   * Adds function vm.hasPermission
   * Adds {@link Role} objects vm.orgRole, vm.projectRole, vm.taskRole, and vm.assetTaskRole.
   * @param {$rootScope.Scope} $scope
   * @param {Object} vm
   * @param {Object} VIEW_ACTIONS Map of action name to required permission
   * @param {modelGetter|Object} taskGetter
   * @param {modelIdGetter|string} taskIdGetter
   * @param {modelIdGetter|string} parentTaskIdGetter
   * @return {Subscription}
   * @deprecated
   */
  function bindAssetTask($scope, vm, VIEW_ACTIONS, taskGetter, taskIdGetter, parentTaskIdGetter) {
    vm.permissions = {};

    vm.hasPermission = noop;

    const parentTask$ = parentTaskIdGetter
      ? RoleBinding.legacyToObservable($scope, parentTaskIdGetter)
        .switchMap(taskId => TaskDomain.$get(taskId))
      : null;

    const assetTask$ = RoleBinding.legacyModelToObservable($scope, taskGetter);

    return rxPermissionsService.buildUserAssetTaskRoleData$(assetTask$, parentTask$)
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .subscribe(assetTaskBinding($scope, vm, VIEW_ACTIONS));
  }

  function assetTaskBinding($scope, vm, VIEW_ACTIONS) {
    const stateFn = rxPermissionsService.assetTaskState(VIEW_ACTIONS);
    return roleData => $scope.$evalAsync(() => Object.assign(vm, stateFn(roleData)));
  }

  // Used for permissions checks since, often, not having permission will result in a firebase error
  function __onError(e) {
    return false;
  }
}

// TODO: move domain types somewhere appropriate

/**
 * A domain class with a relationship members: UserDomain
 *
 * @interface HasMembersDomainClass
 */

/**
 *
 * @function
 * @name HasMembersDomainClass#$getMembersRelationRef
 * @param {String} id
 * @param {String} userId
 * @returns {Firebase} A Firebase reference
 */

/**
 *
 * @function
 * @name HasMembersDomainClass#$getMembersRelation
 * @param {String} id
 * @param {String} userId
 * @returns {Promise.<MembersRelation>} A Firebase reference
 */

/**
 * @typedef {Object} MembersRelation
 * @property {boolean} isActive
 * @property {DomainLink} role
 */

/**
 * @typedef {Object} DomainLink
 * @property {string} id
 * @property {string} collection
 * @property {string} [value] May optionally contain a cached value of the toString result of the target domain instance
 */



// WEBPACK FOOTER //
// ./src/js/auth/services/PermissionsService.js